import React, { useState, useEffect } from 'react';
import { X, Upload, Package, AlertCircle, ChevronRight, ShoppingCart } from 'lucide-react';
import PhotoUpload from '../ui/PhotoUpload';
import { categoryAPI } from '../../services/categoryAPI';
import { tenantConfigAPI } from '../../services/tenantConfigAPI';
import { inventoryAPI } from '../../services/inventoryAPI';
import { InventoryCategory, TrackingType } from '../../types/category';
import { getItemTrackingConfig, getDefaultReorderLevel, requiresExpiration, getDefaultExpirationMonths } from '../../utils/categoryTracking';
import { logger } from '../../utils/logger';

// ✅ LOGGING: Create dedicated logger for AddItemModal
const modalLogger = logger.createServiceLogger('AddItemModal');

interface AddItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (itemData: any) => void;
 // NEW: Purchase integration props
  purchaseId?: string;
  lineItemId?: string;
  suggestedCategory?: string;
  suggestedItemType?: 'SERIALIZED' | 'BULK_TRACKED' | 'CONSUMABLE';
  suggestedQuantity?: number;
  suggestedPurchaseDate?: string;
  titleOverride?: string;
}

const AddItemModal: React.FC<AddItemModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave,
  purchaseId,
  lineItemId,
  suggestedCategory,
  suggestedItemType,
  suggestedQuantity,
  suggestedPurchaseDate,
  titleOverride
}) => {
  const [formData, setFormData] = useState({
    itemType: 'SERIALIZED' as 'SERIALIZED' | 'BULK_TRACKED' | 'CONSUMABLE',
    name: '',
    description: '',
    categoryId: '',
    parentCategoryId: '',
    manufacturer: '',
    model: '',
    purchaseDate: '',
    purchasePrice: '',
    location: '',
    subLocation: '',
    condition: 'Excellent' as 'Excellent' | 'Good' | 'Fair' | 'Poor' | 'Needs Repair',
    serialNumber: '',
    status: 'Available' as 'Available' | 'Assigned' | 'Maintenance' | 'Retired',
    totalQuantity: '',
    availableQuantity: '',
    reorderLevel: '',
    isBodyArmor: false,
    ballisticLevel: '',
    size: '',
    expirationDate: '',
    photos: [] as string[]
  });

  const [categories, setCategories] = useState<InventoryCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [selectedParentCategory, setSelectedParentCategory] = useState<InventoryCategory | null>(null);
  const [availableChildCategories, setAvailableChildCategories] = useState<InventoryCategory[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [tempItemId] = useState(() => `temp_${Date.now()}_${Math.random().toString(36).substring(2)}`);

  // FIXED: Dynamic location loading
  const [availableLocations, setAvailableLocations] = useState<string[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  const fallbackCategories = ['Firearms', 'Electronics', 'Protective Gear', 'Vehicles', 'Medical', 'Communications', 'Tools', 'Uniforms', 'Training Equipment'];
  const ballisticLevels = ['IIA', 'II', 'IIIA', 'III', 'IV'];
  const sizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'];

  // Determine if fields should be locked (coming from purchase)
  const isFromPurchase = Boolean(purchaseId);
  const categoryLocked = isFromPurchase && Boolean(suggestedCategory);
  const itemTypeLocked = isFromPurchase && Boolean(suggestedItemType);

  useEffect(() => {
    if (isOpen) {
      modalLogger.debug('Modal opened, loading categories and locations', {
        isFromPurchase,
        purchaseId,
        suggestedCategory,
        suggestedItemType
      });
      loadCategories();
      loadAvailableLocations();
    }
  }, [isOpen]);

  // NEW: Pre-populate form when coming from purchase
  useEffect(() => {
    if (isOpen && isFromPurchase) {
      modalLogger.debug('Pre-populating form from purchase context', {
        purchaseId,
        suggestedCategory,
        suggestedItemType,
        suggestedQuantity,
        suggestedPurchaseDate
      });

      setFormData(prev => ({
        ...prev,
        itemType: suggestedItemType || prev.itemType,
        categoryId: suggestedCategory || prev.categoryId,
        totalQuantity: suggestedQuantity ? suggestedQuantity.toString() : prev.totalQuantity,
        purchaseDate: suggestedPurchaseDate || prev.purchaseDate
      }));

      // Pre-select parent category if we have a suggested subcategory
      if (suggestedCategory && categories.length > 0) {
        const selectedSubcat = findCategoryById(suggestedCategory);
        if (selectedSubcat?.parentCategoryId) {
          const parent = categories.find(c => c.categoryId === selectedSubcat.parentCategoryId);
          if (parent) {
            setSelectedParentCategory(parent);
            setAvailableChildCategories(parent.children || []);
            setFormData(prev => ({
              ...prev,
              parentCategoryId: selectedSubcat.parentCategoryId || ''
            }));
          }
        }
      }
    }
 }, [isOpen, isFromPurchase, suggestedCategory, suggestedItemType, suggestedQuantity, suggestedPurchaseDate, categories]);

  const loadCategories = async () => {
    const timer = logger.startTimer('AddItemModal', 'loadCategories');
    setLoadingCategories(true);
    
    try {
      modalLogger.debug('Loading hierarchical categories');
      const response = await categoryAPI.getAllCategories();
      
      if (response.success && response.data) {
        const tree = categoryAPI.buildCategoryTree(response.data.categories);
        const categoriesWithChildren: InventoryCategory[] = tree.map(node => ({
          categoryId: node.categoryId,
          name: node.name,
          description: node.description,
          icon: node.icon,
          sortOrder: node.sortOrder,
          isActive: node.isActive,
          itemCount: node.itemCount,
          parentCategoryId: node.parentCategoryId,
          children: node.children.map(child => ({
            categoryId: child.categoryId,
            name: child.name,
            description: child.description,
            icon: child.icon,
            sortOrder: child.sortOrder,
            isActive: child.isActive,
            itemCount: child.itemCount,
            parentCategoryId: child.parentCategoryId
          }))
        }));
        
        modalLogger.info('Categories loaded successfully', {
          categoryCount: categoriesWithChildren.length,
          parentCategories: categoriesWithChildren.filter(c => !c.parentCategoryId).length,
          subcategories: categoriesWithChildren.filter(c => c.parentCategoryId).length
        });
        
        setCategories(categoriesWithChildren);
      } else {
        modalLogger.warn('Failed to load categories, using fallback', {
          error: response.error
        });
      }
    } catch (error: any) {
      modalLogger.error('Error loading categories', {
        error: error.message
      });
    } finally {
      setLoadingCategories(false);
      timer();
    }
  };

  const loadAvailableLocations = async () => {
    setLoadingLocations(true);
    try {
      modalLogger.debug('Loading available locations from tenant configuration');
      const locations = await tenantConfigAPI.getInventoryLocations();
      modalLogger.info('Locations loaded from tenant config', {
        locationCount: locations.length,
        locations: locations
      });
      setAvailableLocations(locations);
    } catch (err: any) {
      modalLogger.error('Error loading locations from tenant config', {
        error: err.message
      });
      setAvailableLocations([
        'Armory', 'Equipment Room', 'Supply Room', 'Communications Room',
        'Vehicle Bay', 'Training Room', 'Evidence Room', 'Admin Office', 'Storage Unit'
      ]);
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleParentCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const parentCategoryId = e.target.value;
    
    modalLogger.debug('Parent category selected', {
      parentCategoryId,
      categoryName: categories.find(c => c.categoryId === parentCategoryId)?.name
    });
    
    const parentCategory = categories.find(cat => cat.categoryId === parentCategoryId);
    setSelectedParentCategory(parentCategory || null);
    
    const childCategories = parentCategory?.children || [];
    setAvailableChildCategories(childCategories);
    
    setFormData(prev => ({
      ...prev,
      parentCategoryId: parentCategoryId,
      categoryId: childCategories.length === 0 ? parentCategoryId : ''
    }));
    
    if (errors.categoryId) {
      setErrors(prev => ({ ...prev, categoryId: '' }));
    }
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const categoryId = e.target.value;
    const selectedCategory = availableChildCategories.find(c => c.categoryId === categoryId);
    
    // Get tracking config from selected category
    const trackingConfig = getItemTrackingConfig(selectedCategory);
    const defaultReorder = getDefaultReorderLevel(selectedCategory);
    
    modalLogger.debug('Subcategory selected', {
      categoryId,
      categoryName: selectedCategory?.name,
      trackingType: trackingConfig.trackingType,
      defaultReorderLevel: defaultReorder
    });
    
    setFormData(prev => ({ 
      ...prev, 
      categoryId,
      itemType: trackingConfig.trackingType,
      reorderLevel: defaultReorder?.toString() || prev.reorderLevel
    }));
    
    if (errors.categoryId) {
      setErrors(prev => ({ ...prev, categoryId: '' }));
    }
  };

  const handlePhotosUpdate = (photoUrls: string[]) => {
    modalLogger.debug('Photos updated', {
      photoCount: photoUrls.length,
      tempItemId
    });
    setFormData(prev => ({ ...prev, photos: photoUrls }));
  };

  const getSubcategoryState = () => {
    if (!formData.parentCategoryId) {
      return {
        disabled: true,
        placeholder: 'Select main category first',
        options: [],
        className: 'flex-1 px-3 py-2 border rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300'
      };
    }
    
    if (availableChildCategories.length === 0) {
      return {
        disabled: true,
        placeholder: 'No subcategories available',
        options: [],
        className: 'flex-1 px-3 py-2 border rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300'
      };
    }
    
    return {
      disabled: categoryLocked,
      placeholder: 'Select subcategory...',
      options: availableChildCategories,
      className: `flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
        categoryLocked ? 'bg-gray-100 cursor-not-allowed' : ''
      } ${errors.categoryId ? 'border-red-500' : 'border-gray-300'}`
    };
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'Item name is required';
    if (!formData.location.trim()) newErrors.location = 'Primary location is required';

    if (categories.length > 0) {
      if (!formData.parentCategoryId) {
        newErrors.categoryId = 'Category is required';
      } else if (availableChildCategories.length > 0 && !formData.categoryId) {
        newErrors.categoryId = 'Subcategory is required';
      } else if (availableChildCategories.length === 0 && !formData.categoryId) {
        setFormData(prev => ({ ...prev, categoryId: formData.parentCategoryId }));
      }
    } else {
      if (!formData.categoryId) newErrors.categoryId = 'Category is required';
    }

    if (formData.itemType === 'SERIALIZED') {
      if (!formData.serialNumber.trim()) newErrors.serialNumber = 'Serial number is required for serialized items';
    }

    if (formData.itemType === 'BULK_TRACKED') {
      if (!formData.totalQuantity || parseInt(formData.totalQuantity) <= 0) {
        newErrors.totalQuantity = 'Total quantity must be greater than 0';
      }
    }

    if (formData.itemType === 'CONSUMABLE') {
      if (!formData.availableQuantity || parseInt(formData.availableQuantity) <= 0) {
        newErrors.availableQuantity = 'Available quantity must be greater than 0';
      }
    }

    if (requiresExpirationTracking() && formData.isBodyArmor) {
      if (!formData.ballisticLevel) newErrors.ballisticLevel = 'Ballistic level is required for body armor';
      if (!formData.size) newErrors.size = 'Size is required for body armor';
      if (!formData.expirationDate) newErrors.expirationDate = 'Expiration date is required for body armor';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

 const requiresExpirationTracking = (): boolean => {
    const selectedCategory = findCategoryById(formData.categoryId);
    return requiresExpiration(selectedCategory);
  };

  const findCategoryById = (categoryId: string): InventoryCategory | undefined => {
    for (const category of categories) {
      if (category.categoryId === categoryId) {
        return category;
      }
      if (category.children) {
        const child = category.children.find((c: InventoryCategory) => c.categoryId === categoryId);
        if (child) return child;
      }
    }
    return undefined;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    modalLogger.info('Submitting new item', {
      itemType: formData.itemType,
      itemName: formData.name,
      categoryId: formData.categoryId,
      parentCategoryId: formData.parentCategoryId,
      selectedParent: selectedParentCategory?.name,
      selectedCategory: findCategoryById(formData.categoryId)?.name,
      photoCount: formData.photos.length,
      isBodyArmor: requiresExpirationTracking() && formData.isBodyArmor,
      location: formData.location,
      purchaseId: purchaseId || 'none'
    });

    const baseItem = {
      name: formData.name,
      description: formData.description || undefined,
      category: categories.length > 0 ? undefined : formData.categoryId,
      categoryId: formData.categoryId,
      parentCategoryId: formData.parentCategoryId || undefined,
      manufacturer: formData.manufacturer || undefined,
      model: formData.model || undefined,
      purchaseDate: formData.purchaseDate || undefined,
      purchasePrice: formData.purchasePrice ? parseFloat(formData.purchasePrice) : undefined,
      location: formData.location,
      subLocation: formData.subLocation || undefined,
      condition: formData.condition,
      photos: formData.photos,
      // NEW: Include purchaseId if creating from purchase
      purchaseId: purchaseId || undefined,
      lineItemId: lineItemId || undefined
    };

    let itemData: any;

    if (formData.itemType === 'SERIALIZED') {
      itemData = {
        ...baseItem,
        itemType: 'SERIALIZED' as const,
        serialNumber: formData.serialNumber,
        status: formData.status
      };

      if (requiresExpirationTracking() && formData.isBodyArmor) {
        (itemData as any).bodyArmor = {
          ballisticLevel: formData.ballisticLevel,
          size: formData.size,
          expirationDate: formData.expirationDate
        };
      }
    } else if (formData.itemType === 'BULK_TRACKED') {
      itemData = {
        ...baseItem,
        itemType: 'BULK_TRACKED' as const,
        totalQuantity: parseInt(formData.totalQuantity),
        availableQuantity: parseInt(formData.totalQuantity),
        reorderLevel: formData.reorderLevel ? parseInt(formData.reorderLevel) : undefined
      };
    } else {
      // CONSUMABLE
      itemData = {
        ...baseItem,
        itemType: 'CONSUMABLE' as const,
        availableQuantity: parseInt(formData.availableQuantity),
        reorderLevel: formData.reorderLevel ? parseInt(formData.reorderLevel) : undefined
      };
    }

    onSave(itemData);
    resetForm();
  };

  const resetForm = () => {
    modalLogger.debug('Resetting form');
   setFormData({
      itemType: 'SERIALIZED',
      name: '',
      description: '',
      categoryId: '',
      parentCategoryId: '',
      manufacturer: '',
      model: '',
      purchaseDate: '',
      purchasePrice: '',
      location: '',
      subLocation: '',
      condition: 'Excellent',
      serialNumber: '',
      status: 'Available',
      totalQuantity: '',
      availableQuantity: '',
      reorderLevel: '',
      isBodyArmor: false,
      ballisticLevel: '',
      size: '',
      expirationDate: '',
      photos: []
    });
    setErrors({});
    setSelectedParentCategory(null);
    setAvailableChildCategories([]);
  };

  const handleClose = () => {
    modalLogger.debug('Modal closed');
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const subcategoryState = getSubcategoryState();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-gray-200" style={{ backgroundColor: '#f8fafc' }}>
          <h2 className="text-xl font-bold text-gray-800">
            {titleOverride || 'Add New Inventory Item'}
          </h2>
          <button onClick={handleClose} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* NEW: Purchase Context Banner */}
        {isFromPurchase && purchaseId && (
          <div className="bg-blue-50 border-b border-blue-200 p-4">
            <div className="flex items-center space-x-2">
              <ShoppingCart className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Creating from Purchase {purchaseId}
                </p>
                <p className="text-xs text-blue-700">
                  This item will be linked to the purchase record for tracking
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Item Type - determined by category selection */}
          <div className={`p-4 rounded-lg ${
            !formData.categoryId ? 'bg-gray-50' :
            formData.itemType === 'SERIALIZED' ? 'bg-blue-50 border border-blue-200' :
            formData.itemType === 'BULK_TRACKED' ? 'bg-green-50 border border-green-200' :
            'bg-orange-50 border border-orange-200'
          }`}>
            <label className="block text-sm font-medium text-gray-700 mb-2">Item Tracking Type</label>
            {!formData.categoryId ? (
              <p className="text-sm text-gray-500 italic">Select a subcategory to determine tracking type</p>
            ) : (
              <div className="flex items-center space-x-2">
                <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                  formData.itemType === 'SERIALIZED' ? 'bg-blue-100 text-blue-800' :
                  formData.itemType === 'BULK_TRACKED' ? 'bg-green-100 text-green-800' :
                  'bg-orange-100 text-orange-800'
                }`}>
                  {formData.itemType === 'SERIALIZED' ? 'Serialized' :
                   formData.itemType === 'BULK_TRACKED' ? 'Bulk Tracked' : 'Consumable'}
                </span>
                <span className="text-sm text-gray-600">
                  {formData.itemType === 'SERIALIZED' ? '— Individual items with serial numbers' :
                   formData.itemType === 'BULK_TRACKED' ? '— Quantity tracked, returns restore stock' :
                   '— Stock level only, returns disposed'}
                </span>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-2">
              Tracking type is determined by the selected subcategory
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Item Name <span className="text-red-500">*</span>
              </label>
              <input type="text" name="name" value={formData.name} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-300'}`} placeholder="e.g., Glock 19 Gen5" />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              
              {loadingCategories ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
                  Loading categories...
                </div>
              ) : categories.length > 0 ? (
                <div className="space-y-2">
                  <select 
                    value={formData.parentCategoryId} 
                    onChange={handleParentCategoryChange} 
                    disabled={categoryLocked}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                      categoryLocked ? 'bg-gray-100 cursor-not-allowed' : ''
                    } ${errors.categoryId ? 'border-red-500' : 'border-gray-300'}`}
                  >
                    <option value="">Select main category...</option>
                    {categories.map(category => (
                      <option key={category.categoryId} value={category.categoryId}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  
                  <div className="flex items-center space-x-2">
                    <ChevronRight className={`w-4 h-4 ${subcategoryState.disabled ? 'text-gray-300' : 'text-gray-400'}`} />
                    <select 
                      value={formData.categoryId}
                      onChange={handleCategoryChange}
                      disabled={subcategoryState.disabled}
                      className={subcategoryState.className}
                    >
                      <option value="">{subcategoryState.placeholder}</option>
                      {subcategoryState.options.map(child => (
                        <option key={child.categoryId} value={child.categoryId}>
                          {child.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {selectedParentCategory && (
                    <div className="text-xs text-gray-500 flex items-center space-x-1">
                      <span>Selected:</span>
                      <span className="font-medium">{selectedParentCategory.name}</span>
                      {formData.categoryId && formData.categoryId !== formData.parentCategoryId && (
                        <>
                          <ChevronRight className="w-3 h-3" />
                          <span className="font-medium">{findCategoryById(formData.categoryId)?.name}</span>
                        </>
                      )}
                    </div>
                  )}
                  
                  {categoryLocked && (
                    <p className="text-xs text-gray-500">
                      Category is set by purchase context
                    </p>
                  )}
                </div>
              ) : (
                <select name="categoryId" value={formData.categoryId} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.categoryId ? 'border-red-500' : 'border-gray-300'}`}>
                  <option value="">Select category...</option>
                  {fallbackCategories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              )}
              
              {errors.categoryId && <p className="text-red-500 text-xs mt-1">{errors.categoryId}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer</label>
              <input type="text" name="manufacturer" value={formData.manufacturer} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="e.g., Glock, Motorola" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
              <input type="text" name="model" value={formData.model} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="e.g., G19 Gen5" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Date</label>
              <input type="date" name="purchaseDate" value={formData.purchaseDate} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Price</label>
              <input type="number" name="purchasePrice" value={formData.purchasePrice} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="0.00" step="0.01" min="0" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Primary Location <span className="text-red-500">*</span>
              </label>
              {loadingLocations ? (
                <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
                  Loading locations...
                </div>
              ) : (
                <select 
                  name="location" 
                  value={formData.location} 
                  onChange={handleInputChange} 
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.location ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="">Select primary location...</option>
                  {availableLocations.map(location => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
              )}
              {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Specific Location
                <span className="text-gray-500 text-xs ml-1">(Shelf, Rack, Bin, etc.)</span>
              </label>
              <input type="text" name="subLocation" value={formData.subLocation} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="e.g., Vault A - Shelf 3, Rack B2, Bin 15" />
              <p className="text-xs text-gray-500 mt-1">
                Examples: "Vault A - Shelf 3", "Rack B2", "Locker 47", "Bin 15-C"
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
              <select name="condition" value={formData.condition} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="Excellent">Excellent</option>
                <option value="Good">Good</option>
                <option value="Fair">Fair</option>
                <option value="Poor">Poor</option>
                <option value="Needs Repair">Needs Repair</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea name="description" value={formData.description} onChange={handleInputChange} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Optional description or notes..." />
          </div>

          {formData.itemType === 'SERIALIZED' && (
            <div className="bg-blue-50 p-4 rounded-lg space-y-4">
              <h3 className="font-medium text-gray-800">Serialized Item Details</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Serial Number <span className="text-red-500">*</span>
                  </label>
                  <input type="text" name="serialNumber" value={formData.serialNumber} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.serialNumber ? 'border-red-500' : 'border-gray-300'}`} placeholder="Enter serial number" />
                  {errors.serialNumber && <p className="text-red-500 text-xs mt-1">{errors.serialNumber}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Initial Status</label>
                  <select name="status" value={formData.status} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="Available">Available</option>
                    <option value="Assigned">Assigned</option>
                    <option value="Maintenance">Maintenance</option>
                    <option value="Retired">Retired</option>
                  </select>
                </div>
              </div>
            </div>
          )}

         {formData.itemType === 'BULK_TRACKED' && (
            <div className="bg-green-50 p-4 rounded-lg space-y-4">
              <h3 className="font-medium text-gray-800">Bulk Tracked Item Details</h3>
              <p className="text-xs text-gray-600">Track who has what. Returns restore inventory.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Quantity <span className="text-red-500">*</span>
                  </label>
                  <input type="number" name="totalQuantity" value={formData.totalQuantity} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.totalQuantity ? 'border-red-500' : 'border-gray-300'}`} placeholder="0" min="1" />
                  {errors.totalQuantity && <p className="text-red-500 text-xs mt-1">{errors.totalQuantity}</p>}
                  <p className="text-xs text-gray-500 mt-1">Available quantity will be set equal to total on create</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reorder Level</label>
                  <input type="number" name="reorderLevel" value={formData.reorderLevel} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="0" min="0" />
                  <p className="text-xs text-gray-500 mt-1">Alert when stock falls to this level</p>
                </div>
              </div>
            </div>
          )}

          {formData.itemType === 'CONSUMABLE' && (
            <div className="bg-orange-50 p-4 rounded-lg space-y-4">
              <h3 className="font-medium text-gray-800">Consumable Item Details</h3>
              <p className="text-xs text-gray-600">Stock level only. Returns are disposed, not restocked.</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Available Quantity <span className="text-red-500">*</span>
                  </label>
                  <input type="number" name="availableQuantity" value={formData.availableQuantity} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.availableQuantity ? 'border-red-500' : 'border-gray-300'}`} placeholder="0" min="1" />
                  {errors.availableQuantity && <p className="text-red-500 text-xs mt-1">{errors.availableQuantity}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reorder Level</label>
                  <input type="number" name="reorderLevel" value={formData.reorderLevel} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="0" min="0" />
                  <p className="text-xs text-gray-500 mt-1">Alert when stock falls to this level</p>
                </div>
              </div>
            </div>
          )}

          {requiresExpirationTracking() && formData.itemType === 'SERIALIZED' && (
            <div className="bg-amber-50 p-4 rounded-lg space-y-4">
              <div className="flex items-center space-x-2">
                <input type="checkbox" name="isBodyArmor" checked={formData.isBodyArmor} onChange={handleInputChange} className="rounded" />
                <label className="text-sm font-medium text-gray-700">This item requires expiration tracking</label>
              </div>

              {formData.isBodyArmor && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ballistic Level <span className="text-red-500">*</span>
                    </label>
                    <select name="ballisticLevel" value={formData.ballisticLevel} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.ballisticLevel ? 'border-red-500' : 'border-gray-300'}`}>
                      <option value="">Select level...</option>
                      {ballisticLevels.map(level => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                    {errors.ballisticLevel && <p className="text-red-500 text-xs mt-1">{errors.ballisticLevel}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Size <span className="text-red-500">*</span>
                    </label>
                    <select name="size" value={formData.size} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.size ? 'border-red-500' : 'border-gray-300'}`}>
                      <option value="">Select size...</option>
                      {sizes.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                    {errors.size && <p className="text-red-500 text-xs mt-1">{errors.size}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expiration Date <span className="text-red-500">*</span>
                    </label>
                    <input type="date" name="expirationDate" value={formData.expirationDate} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.expirationDate ? 'border-red-500' : 'border-gray-300'}`} />
                    {errors.expirationDate && <p className="text-red-500 text-xs mt-1">{errors.expirationDate}</p>}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-800 mb-3">Photos (Optional)</h3>
            <PhotoUpload itemId={tempItemId} photos={formData.photos} onPhotosUpdate={handlePhotosUpdate} maxPhotos={5} />
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button type="button" onClick={handleClose} className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 text-white rounded-lg transition-colors" style={{ backgroundColor: '#082759' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#0a2e6b'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#082759'}>
              Add Item
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddItemModal;